import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import ExpanedMore from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/CheckCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { useSnackbar } from 'material-ui-snackbar-provider';
import ExpandLess from '@mui/icons-material/ExpandLess';
import { useHistory } from 'react-router-dom';
import cx from 'classnames';
import DeleteIcon from '@mui/icons-material/DeleteForeverOutlined';

import { fetchNotificationVideoFeedData, getAllVideofeedsWithoutPagination, postFolderViewRequest, postVideofeedViewRequest } from '../../../../api';
import { deleteNotifications, getAllNotifications, patchNotifications } from '../../../../api/notificationsApi';
import { NOTIFICATION_CATEGORIES, NOTIFICATION_TYPES, TNOTIFICATION } from '../../../../enums/NOTIFICATION';
import { ReactComponent as TimelineIcon } from '../../../../assets/icons/timeline.svg';
import { updateUnreadCount, updateNotificationClick, updateShowWarningModal } from '../../../../store/sharedStore/sharedStoreSlice';
import { ReactComponent as ChatIcon } from '../../../../assets/icons/chat.svg';
import LinearProgress from '../../../../components/modules/LinearProgress';
import { PATHS, VIDEOFEED_TYPE, VIDEOPAGE_TYPE } from '../../../../enums';
import { ContainedButton } from '../../../../components/modules/Buttons';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import { resetTimelineState } from '../../../../store/videos/videoSlice';
import useOnClickOutside from '../../../../helpers/useOnClickOutside';
import FolderIcon from '../../../../assets/icons/folder-open.svg';
import ComposerIcon from '../../../../assets/icons/timeline-toolbar/create-compos.svg';
import Avatar from '../../../../components/modules/Avatar';
import { formatRelativeTime } from '../../../../helpers';
import css from './Notifications.module.scss';
import { checkContentAvailability } from '../../../../api/accessesApi';

const HighlightSubstring = ({ text, substring }: { text: string; substring: string }) => {
  const startIndex = text.indexOf(substring);

  if (startIndex === -1) {
    return <span>{text}</span>;
  }

  return (
    <span>
      {text.substring(0, startIndex)}
      <span className={css.highlight}>{text.substring(startIndex, startIndex + substring.length)}</span>
      {text.substring(startIndex + substring.length)}
    </span>
  );
};

type TNotifications = {
  toggleOff: () => void;
  open: boolean;
};

let fetchLoading = false; // using this boolean, because setState => is not triggered immediatly

const Notifications: React.FC<TNotifications> = ({ toggleOff, open }) => {
  const canViewEmails = useCanViewEmails();
  const [notifications, setNotifications] = useState<TNOTIFICATION[]>([]);
  const notification = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [showUnread, setShowUnread] = useState(false);
  const { currentUser } = useAppSelector((state) => state.user);
  const { notificationTrigger } = useAppSelector((state) => state.sharedStore);
  const history = useHistory();
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (!open) {
      setShowUnread(false);
      fetchData();
    }
  }, [open]);

  const fetchData = async () => {
    const retries = 10;
    for (let attempt = 1; attempt <= retries; attempt++) {
      try {
        setLoading(true);
        const { data } = await getAllNotifications(true);
        const notifArr = data;
        let howManyUnread = 0;

        data.forEach((notif) => {
          if (!notif?.isRead) {
            howManyUnread += 1;
          }
          if (notif?.child_notifications?.length) {
            notif?.child_notifications.forEach((child) => {
              if (!child?.isRead) {
                howManyUnread += 1;
              }
            });
          }
        });

        setNotifications(notifArr);
        dispatch(updateUnreadCount(howManyUnread));
        break;
      } catch (err) {
        if (attempt === retries) {
          console.log('Request failed after 10 attempts:', err);
          break;
        }
        console.log(`Attempt ${attempt} failed. Retrying...`);
        await new Promise((resolve) => setTimeout(resolve, 5000));
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (notificationTrigger) {
      fetchData();
    }
  }, [notificationTrigger]);

  const onDeleteAll = async () => {
    try {
      setLoading(true);
      await deleteNotifications(notifications[0].id, true);
      setNotifications([]);
      await fetchData();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const readAllMessages = async () => {
    if (notifications.length === 0) return;
    try {
      setLoading(true);
      await patchNotifications(notifications[0].id, true);
      await fetchData();
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const readMessage = async (id: string) => {
    if (notifications.length === 0) return;
    try {
      setLoading(true);
      await patchNotifications(id, false);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const resolveTimelineRedirectFeed = async (videofeed: any, folderIdFallback?: string | number) => {
    const resolvedFolderId = folderIdFallback || videofeed?.folder?.id || videofeed?.folder_id;
    if (!videofeed || videofeed.type !== VIDEOFEED_TYPE.TIMELINE || !resolvedFolderId) {
      return {
        targetFeed: videofeed,
        targetFolderId: resolvedFolderId,
      };
    }

    try {
      const groupedFeeds = await getAllVideofeedsWithoutPagination(String(resolvedFolderId));
      const groupedFeed = groupedFeeds?.documents?.find((feed: any) => String(feed.id) === String(videofeed.id));
      const referenceTimeline =
        groupedFeed?.reference_timelines?.find((feed: any) => String(feed.id) !== String(videofeed.id)) ||
        groupedFeed?.reference_timelines?.[0];

      if (referenceTimeline && !(groupedFeed?.topics?.length || 0)) {
        return {
          targetFeed: referenceTimeline,
          targetFolderId: referenceTimeline?.folder?.id || referenceTimeline?.folder_id || resolvedFolderId,
        };
      }
    } catch (error) {
      console.warn('Failed to resolve notification redirect feed', error);
    }

    return {
      targetFeed: videofeed,
      targetFolderId: resolvedFolderId,
    };
  };

  const redirectUser = async (obj: TNOTIFICATION, type: NOTIFICATION_CATEGORIES, timeStamp?: number) => {
    if (fetchLoading) return;
    fetchLoading = true;

    dispatch(resetTimelineState());
    const commentThreadParams = (() => {
      const comment = obj?.comment;
      if (!comment?.id) return '';
      const isReply = comment?.parent_type === 'comment';
      const commentId = isReply ? comment?.parent_id : comment?.id;
      const replyId = isReply ? comment?.id : '';
      if (!commentId) return '';
      return `&commentId=${commentId}${replyId ? `&replyId=${replyId}` : ''}`;
    })();
    let shared = false;
    if (obj?.videofeed && obj?.videofeed.created_by?.id! != currentUser?.id) {
      shared = true;
    }
    setLoading(true);
    await readMessage(obj.id);
    await fetchData();

    try {
      // pre-check access/deleted state for videofeed/folder ---
      // Check videofeed for VIDEOFEED + COMMENT notifications
      if (
        (type === NOTIFICATION_CATEGORIES.VIDEOFEED || type === NOTIFICATION_CATEGORIES.COMMENT) &&
        obj?.videofeed &&
        obj?.type !== NOTIFICATION_TYPES.DELETED_CONTENT
      ) {
        const ok = await checkContentBeforeRedirect('videofeed', +obj.videofeed.id);
        if (!ok) return; // message already shown
      }

      // Check folder for FOLDER notifications
      if (type === NOTIFICATION_CATEGORIES.FOLDER && obj?.folder && obj?.type !== NOTIFICATION_TYPES.DELETED_CONTENT) {
        const ok = await checkContentBeforeRedirect('folder', +obj.folder.id);
        if (!ok) return; // message already shown
      }

      // ---------------------------------------------------------------

      toggleOff();

      if (type === NOTIFICATION_CATEGORIES.VIDEOFEED) {
        const customValue = obj?.custom_value ? JSON.parse(obj.custom_value) : {};
        const {
          mode = '',
          prefix = '',
          formatForAnglesImport = '',
          transcode_batch_uuid = '',
          target = '',
          destinationFolderId,
        } = customValue;
        if (target === 'my_folders') {
          shared = false;
        } else if (target === 'shared_with_me') {
          shared = true;
        }
        const resolvedFolderId = destinationFolderId || obj?.videofeed?.folder?.id;
        const extra =
          (mode ? `&mode=${mode}` : '') +
          (prefix ? `&prefix=${prefix}` : '') +
          (formatForAnglesImport ? `&formatForAnglesImport=${formatForAnglesImport}` : '') +
          (transcode_batch_uuid ? `&transcode_batch_uuid=${transcode_batch_uuid}` : '');

        postVideofeedViewRequest(obj!.videofeed!.id);
        if (obj?.type === NOTIFICATION_TYPES.DELETED_CONTENT) {
          let path;
          switch (obj?.videofeed.type) {
            case VIDEOFEED_TYPE.MY_COMPOSER:
              path = PATHS.MY_REFLEX_COMPOSERS.path;
              break;
            default:
              path = `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${obj.videofeed.folder.id}&pageType=${PATHS.MY_FOLDERS.label}`;
              break;
          }
          history.push(path);
        } else if (obj?.videofeed.type === VIDEOFEED_TYPE.MY_COMPOSER) {
          const res = await fetchNotificationVideoFeedData(obj.videofeed.id);
          let shared = false;
          if (currentUser?.id && +res?.data?.created_by?.id === +currentUser?.id) {
            shared = false;
          } else {
            shared = true;
          }
          if (target === 'my_folders') {
            shared = false;
          } else if (target === 'shared_with_me') {
            shared = true;
          }
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${res.data.id}&fileside=true&feed=0&clipId=${res.data.videos[0].id}&clip=0&parentPage=${
              shared ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label
            }&pageType=${VIDEOPAGE_TYPE.MY_COMPOSER}${shared ? '&sharedComposer=true' : ''}` + extra,
          );
        } else if (obj?.videofeed.type === VIDEOFEED_TYPE.COMPOSER && shared) {
          const res = await fetchNotificationVideoFeedData(obj.videofeed.id);
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${obj?.videofeed.id}&folderId=${resolvedFolderId}&fileside=true&feed=0&clipId=${res?.data.videos[0].id}&clip=0&parentPage=${PATHS.SHARED_WITH_ME.label}&pageType=${VIDEOPAGE_TYPE.COMPOSER}&shared=true` +
              extra,
          );
        } else if (obj?.videofeed.type === VIDEOFEED_TYPE.COMPOSER && !shared) {
          const res = await fetchNotificationVideoFeedData(obj.videofeed.id);
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${obj?.videofeed.id}&folderId=${resolvedFolderId}&fileside=true&feed=0&clipId=${res?.data.videos[0].id}&clip=0&parentPage=${PATHS.MY_FOLDERS.label}&pageType=${VIDEOPAGE_TYPE.COMPOSER}` +
              extra,
          );
        } else {
          const { targetFeed, targetFolderId } = await resolveTimelineRedirectFeed(obj.videofeed, resolvedFolderId);
          const isSharedTimeline = targetFeed?.created_by?.id !== currentUser?.id;
          const finalShared =
            target === 'my_folders' ? false : target === 'shared_with_me' ? true : isSharedTimeline;
          history.push(
            `${PATHS.TIMELINE.path}?feedId=${targetFeed.id}&folderId=${targetFolderId}&fileside=true&feed=0&clip=0&parentPage=${
              finalShared ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label
            }&pageType=${VIDEOPAGE_TYPE.TIMELINE}${finalShared ? '&shared=true' : ''}`,
          );
        }
      } else if (type === NOTIFICATION_CATEGORIES.FOLDER) {
        if (obj?.type === NOTIFICATION_TYPES.DELETED_CONTENT) {
          history.push(PATHS.MY_FOLDERS.path);
        } else {
          let customValue: any = {};
          try {
            customValue = obj?.custom_value ? JSON.parse(obj.custom_value) : {};
          } catch {
            customValue = {};
          }
          postFolderViewRequest(obj!.folder!.id);
          if (customValue?.target === 'my_folders') {
            history.push(`${PATHS.VIDEOFEEDS_LIST.path}?folderId=${obj!.folder!.id}&parentPage=${PATHS.MY_FOLDERS.label}`);
          } else {
            history.push(`${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${obj!.folder!.id}&parentPage=${PATHS.SHARED_WITH_ME.label}`);
          }
        }
      } else if (type === NOTIFICATION_CATEGORIES.COMMENT) {
        postVideofeedViewRequest(obj!.videofeed!.id);
        const { data } = await fetchNotificationVideoFeedData(obj.videofeed.id);
        const videofeed = data;
        const ifSharedWithMe = obj.videofeed.created_by.id !== currentUser!.id;
        if (videofeed.type === VIDEOFEED_TYPE.COMPOSER) {
          const clipToJump = `&clipToJump=${obj.comment?.clip?.id || obj.comment?.parent_id}`;
          const isVideoComment = !obj.comment?.clip?.id && obj.comment?.parent_type !== 'clip';
          const commentTab = isVideoComment ? '&commentTab=video' : '';
          const autoPlay = '&autoplay=1';
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${videofeed.id}&folderId=${videofeed.folder.id}&fileside=true&feed=${0}&clipId=${
              videofeed.videos[0].id
            }&clip=0&parentPage=${ifSharedWithMe ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label}&pageType=${
              VIDEOPAGE_TYPE.COMPOSER
            }${ifSharedWithMe && '&sharedComposer=true'}${clipToJump ? clipToJump : ''}${commentTab}${autoPlay}${commentThreadParams}`,
          );
        } else if (videofeed.type === VIDEOFEED_TYPE.MY_COMPOSER) {
          let clipToJump = '';
          if (obj.comment?.clip?.id) {
            clipToJump = `&clipToJump=${obj.comment.clip?.id}`;
          } else if (obj.comment?.parent_type === 'clip' && obj.comment.parent_id) {
            clipToJump = `&clipToJump=${obj.comment.parent_id}`;
          }
          const isVideoComment = !obj.comment?.clip?.id && obj.comment?.parent_type !== 'clip';
          const commentTab = isVideoComment ? '&commentTab=video' : '';
          const autoPlay = '&autoplay=1';
          history.push(
            `${PATHS.COMPOSER_PAGE.path}?feedId=${videofeed.id}&fileside=true&feed=${0}&clipId=${
              videofeed.videos[0].id
            }&clip=0&parentPage=${ifSharedWithMe ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label}&pageType=${
              VIDEOFEED_TYPE.MY_COMPOSER
            }${ifSharedWithMe && '&sharedComposer=true'}${clipToJump ? clipToJump : ''}${commentTab}${autoPlay}${commentThreadParams}`,
          );
        } else {
          let timeToJump = 0;
          if (timeStamp) {
            timeToJump = timeStamp;
          } else if (obj.comment?.timestamp) {
            timeToJump = obj.comment.timestamp;
          }
          let clipToJump = '';
          const isVideoComment = !obj.comment?.clip?.id && obj.comment?.parent_type !== 'clip';
          const commentTab = isVideoComment ? '&commentTab=video' : '';
          const autoPlay = '&autoplay=1';
          if (obj.comment?.clip?.id) {
            clipToJump = `&clipToJump=${obj.comment.clip?.id}`;
            timeToJump = 0;
          }
          dispatch(updateNotificationClick(true));
          history.push(
            `${PATHS.TIMELINE.path}?feedId=${videofeed.id}&folderId=${videofeed.folder.id}&fileside=true&feed=${0}&clip=${0}&clipId=${
              videofeed.videos[0].id
            }${videofeed.created_by.email !== currentUser?.email && '&shared=true'}&parentPage=${
              ifSharedWithMe ? PATHS.SHARED_WITH_ME.label : PATHS.MY_FOLDERS.label
            }&pageType=${VIDEOPAGE_TYPE.TIMELINE}${timeToJump ? `&timeToJump=${timeToJump}` : ''}${clipToJump}${commentTab}${autoPlay}${commentThreadParams}
            `,
          );
        }
      }
    } catch (err: any) {
      console.log(err);
      if (err?.response?.data?.message) {
        notification.showMessage(`${err?.response?.data?.message} (Or you dont have access)`);
      }
    } finally {
      fetchLoading = false;
      setLoading(false);
    }
  };

  const [showMoreId, setShowMoreId] = useState('');
  const switchShowMoreId = (id: string) => {
    setShowMoreId(id);
  };

  const optionMenuRef = useRef<HTMLDivElement | null>(null);
  const switchUnread = async (bool: boolean) => {
    setShowUnread(bool);
    if (bool) {
      const unreadArr = notifications.filter((notif) => !notif.isRead || !!notif.child_notifications.find((child) => !child.isRead));
      setNotifications(unreadArr);
    } else {
      await fetchData();
    }
    onShowAll(false);
  };

  const [showOptions, setShowOptions] = useState(false);

  const onShowAll = (b: boolean) => {
    setShowOptions(b);
  };
  useOnClickOutside(optionMenuRef, (e) => {
    e.stopPropagation();
    if (showOptions) {
      onShowAll(false);
    }
  });

  const onNotifDelete = async (id: string) => {
    if (loading) return;
    try {
      setLoading(true);
      await deleteNotifications(id);
      await fetchData();
    } finally {
      setLoading(false);
    }
  };

  const checkContentBeforeRedirect = async (type: 'folder' | 'videofeed', id: number) => {
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(updateShowWarningModal({ show: true, warningText: 'The content you are trying to access has been deleted' }));
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({ show: true, warningText: 'You do not have permission to access the content you are trying to view.' }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName ? ` The content is from ${res.data.organizationName}.` : ''
            }`,
          }),
        );
      }
      return !res.data?.deleted && !res.data?.noAccess;
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <div className={css.container} role="presentation">
      <div className={css.head}>
        <span className={css.header}>Notifications</span>
        {loading && <LinearProgress />}
        <div className={css.checkBox} onClick={() => onShowAll(!showOptions)}>
          <span className={css.clickUnread}>{!showUnread ? 'All' : 'Unread'}</span>
          {showUnread ? <ExpandLess /> : <ExpanedMore />}

          {showOptions && (
            <div className={css.allDialog} ref={optionMenuRef}>
              <div onClick={() => switchUnread(false)} className={cx(css.option, !showUnread ? css.choosenDialog : '')}>
                All{' '}
              </div>
              <div onClick={() => switchUnread(true)} className={cx(css.option, showUnread ? css.choosenDialog : '')}>
                Unread{' '}
              </div>
            </div>
          )}
        </div>
        <button
          type="button"
          className={css.mobileClose}
          onClick={(e) => {
            e.stopPropagation();
            toggleOff();
          }}
          aria-label="Close notifications"
        >
          <CloseIcon />
        </button>
      </div>
      <div className={css.list}>
        {notifications.map((parentNotif, i) => {
          if (parentNotif.type === NOTIFICATION_TYPES.DOWNLOAD_TRANSCODE_EVENT) {
            return (
              <div className={cx(css.listItem, !parentNotif.isRead && css.folderUnread)} key={`${parentNotif.id}_${i}`}>
                <div className={css.item}>
                  <div className={css.itemTop}>
                    {parentNotif?.videofeed && (
                      <div className={css.description} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                        <DeleteIcon
                          className={css.deleteIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNotifDelete(parentNotif.id);
                          }}
                        />
                        <div style={{ fontStyle: 'italic', fontWeight: 600, marginTop: 4 }}>Online composer is ready to download:</div>
                        <div className={css.message} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.TIMELINE && <TimelineIcon />}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.MY_COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          <div>{parentNotif.videofeed.title}</div>
                        </div>
                        <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (parentNotif.type === NOTIFICATION_TYPES.TRANSCODE_EVENT) {
            const transcodeMessage =
              parentNotif.message?.replace(/^Processing finished for\s*/i, '') ||
              parentNotif.videofeed?.title;
            return (
              <div className={cx(css.listItem, !parentNotif.isRead && css.folderUnread)} key={`${parentNotif.id}_${i}`}>
                <div className={css.item}>
                  <div className={css.itemTop}>
                    {parentNotif?.videofeed && (
                      <div className={css.description} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                        <DeleteIcon
                          className={css.deleteIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNotifDelete(parentNotif.id);
                          }}
                        />
                        <div>
                          {parentNotif?.message?.includes('Processing finished') && (
                            <div>
                              <div style={{ fontStyle: 'italic', fontWeight: 600, marginTop: 4 }}>Processing finished for:</div>
                            </div>
                          )}
                        </div>
                        <div className={css.message} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.TIMELINE && <TimelineIcon />}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.MY_COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          <div>{transcodeMessage}</div>
                        </div>
                        <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else if (
            parentNotif.category === NOTIFICATION_CATEGORIES.COMMENT ||
            parentNotif.type.includes('reply') ||
            parentNotif.type.includes('comment')
          ) {
            return (
              <div className={cx(css.listItem, !parentNotif.isRead ? css.unreadListItem : '')} key={`${parentNotif.id}_${i}`}>
                <div className={css.parentItem}>
                  <div className={cx(css.itemTop, !parentNotif.isRead && css.unread)}>
                    <div onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.COMMENT)}>
                      <Avatar avatar={parentNotif?.created_by?.avatar_url} name={parentNotif?.created_by?.email} className={css.avatar} />
                    </div>
                    <div className={css.description}>
                      <DeleteIcon
                        className={css.deleteIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotifDelete(parentNotif.id);
                        }}
                      />
                      <div onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.COMMENT)}>
                        <div style={{ fontWeight: parentNotif?.isRead ? '400' : '600' }}>
                          <div className={css.whoTagged}>
                            {parentNotif?.created_by?.username ? parentNotif?.created_by?.username : (canViewEmails ? parentNotif?.created_by?.email : 'User')}{' '}
                          </div>{' '}
                          {parentNotif?.type === NOTIFICATION_TYPES.TAGGED_COMMENT && 'tagged you in the content'}{' '}
                          {parentNotif?.type === NOTIFICATION_TYPES.TAGGED_REPLY && 'replied in'}{' '}
                          {parentNotif?.type === NOTIFICATION_TYPES.REPLY && 'replied in'}{' '}
                          <span className={css.purple} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.COMMENT)}>
                            "{parentNotif?.videofeed?.title}"{' '}
                          </span>
                        </div>
                        <div className={css.message} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.COMMENT)}>
                          <ChatIcon style={{ marginRight: '8px', marginLeft: '4px' }} />
                          <div className={css.cssWordWrapper}>
                            <HighlightSubstring
                              text={parentNotif.message}
                              substring={currentUser?.username ? `@${currentUser?.username}` : ''}
                            />
                          </div>
                        </div>
                        <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                      </div>
                      {parentNotif?.child_notifications?.length > 0 && showMoreId !== parentNotif.id && (
                        <div
                          className={cx(
                            css.moreMentionsLine,
                            !!parentNotif.child_notifications.find((c) => !c.isRead) && css.moreMentionsLineUnread,
                          )}
                          onClick={() => switchShowMoreId(parentNotif.id)}
                        >
                          <Avatar
                            avatar={parentNotif?.child_notifications[0]?.created_by?.avatar_url}
                            name={parentNotif?.child_notifications[0]?.created_by?.email}
                            className={css.moreAvatar}
                          />
                          <span className={cx(css.line, !!parentNotif.child_notifications.find((c) => !c.isRead) && css.whiteLine)}>
                            +{parentNotif?.child_notifications.length} replies
                          </span>
                        </div>
                      )}
                    </div>
                  </div>

                  <>
                    {parentNotif?.child_notifications?.length > 0 &&
                      showMoreId === parentNotif.id &&
                      parentNotif.child_notifications.map((child, childIndex) => (
                        <div className={cx(css.moreExpanded, !child.isRead && css.childUnread)} key={`${child.id}_${childIndex}`}>
                          <div className={css.drawer} />
                          <div
                            className={css.item}
                            onClick={() => {
                              if (parentNotif.videofeed.type === VIDEOFEED_TYPE.TIMELINE) {
                                redirectUser(child as any, NOTIFICATION_CATEGORIES.COMMENT, parentNotif.comment?.timestamp);
                              } else {
                                redirectUser(child as any, NOTIFICATION_CATEGORIES.COMMENT);
                              }
                            }}
                          >
                            <div className={css.itemTop}>
                              <div className={css.relativeContainer}>
                                <Avatar avatar={child?.created_by?.avatar_url} name={child?.created_by?.email} className={css.avatar} />
                              </div>
                              <div className={css.description}>
                                <DeleteIcon
                                  className={css.deleteIcon}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onNotifDelete(child.id);
                                  }}
                                />
                                <div style={{ fontWeight: parentNotif?.isRead ? '400' : '600' }}>
                                  <div className={css.whoTaggedName}>{child?.created_by.username} </div>{' '}
                                  {child?.type === NOTIFICATION_TYPES.TAGGED_COMMENT && 'tagged you in comment to the content'}{' '}
                                  {child?.type === NOTIFICATION_TYPES.TAGGED_REPLY && 'tagged you in the comment replies to the content'}{' '}
                                  {child?.type === NOTIFICATION_TYPES.REPLY && 'replied to comment in the content'}{' '}
                                  <span className={css.whoTagged}>"{child.videofeed.title}" </span>
                                </div>
                                <div className={css.date}>{formatRelativeTime(new Date(child.created_at))}</div>
                                <div className={css.message}>
                                  <ChatIcon style={{ marginRight: '8px', marginLeft: '4px' }} />
                                  <div className={css.cssWordWrapper}>
                                    <HighlightSubstring
                                      text={child.message}
                                      substring={currentUser?.username ? `@${currentUser?.username}` : ''}
                                    />
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    {parentNotif?.child_notifications.length > 0 && showMoreId === parentNotif.id && (
                      <div className={css.showLess} onClick={() => switchShowMoreId('')}>
                        show less
                      </div>
                    )}
                  </>
                </div>
              </div>
            );
          } else if (
            parentNotif.category === NOTIFICATION_CATEGORIES.VIDEOFEED ||
            parentNotif.category === NOTIFICATION_CATEGORIES.FOLDER
          ) {
            return (
              <div className={cx(css.listItem, !parentNotif.isRead && css.folderUnread)} key={`${parentNotif.id}_${i}`}>
                <div className={css.item}>
                  <div className={css.itemTop}>
                    <Avatar avatar={parentNotif?.created_by?.avatar_url} name={parentNotif?.created_by?.email} className={css.avatar} />
                    {parentNotif?.folder && (
                      <div className={css.description} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.FOLDER)}>
                        <DeleteIcon
                          className={css.deleteIcon}
                          onClick={(e) => {
                            e.stopPropagation();
                            onNotifDelete(parentNotif.id);
                          }}
                        />
                        <div style={{ fontWeight: parentNotif?.isRead ? '400' : '600' }}>
                          <div className={css.whoTagged}>
                            {parentNotif?.created_by?.username ? parentNotif?.created_by?.username : (canViewEmails ? parentNotif?.created_by?.email : 'User')}{' '}
                          </div>{' '}
                          {!parentNotif?.shared_content_message && parentNotif?.message}
                          {!!parentNotif?.shared_content_message?.length && (
                            <div>
                              <div style={{ fontStyle: 'italic', fontWeight: 600 }}>{parentNotif.shared_content_message}</div>
                            </div>
                          )}
                        </div>
                        <div className={css.message} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.FOLDER)}>
                          <img style={{ width: '17px' }} src={FolderIcon} alt="folder" />{' '}
                          <span className={css.whoTagged}> {parentNotif.folder.name}</span>
                        </div>
                        <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                      </div>
                    )}
                    {parentNotif?.videofeed && (
                      <div className={css.description} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                        <div>
                          <div className={css.whoTagged}>
                            {parentNotif?.created_by?.username ? parentNotif?.created_by?.username : (canViewEmails ? parentNotif?.created_by?.email : 'User')}{' '}
                          </div>{' '}
                          {!parentNotif?.shared_content_message?.length && parentNotif?.message}
                          {!!parentNotif?.shared_content_message?.length && (
                            <div>
                              <div style={{ fontStyle: 'italic', fontWeight: 600 }}>{parentNotif.shared_content_message}</div>
                            </div>
                          )}
                        </div>
                        <div className={css.message} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.VIDEOFEED)}>
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.TIMELINE && <TimelineIcon />}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          {parentNotif.videofeed.type === VIDEOFEED_TYPE.MY_COMPOSER && (
                            <img style={{ height: '15px' }} src={ComposerIcon} alt="videofeed" />
                          )}
                          <span>{parentNotif.videofeed.title}</span>
                        </div>
                        <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          } else {
            return (
              <div className={cx(css.listItem, !parentNotif.isRead && css.folderUnread)} key={`${parentNotif.id}_${i}`}>
                <div className={css.item}>
                  <div className={css.itemTop}>
                    <Avatar avatar={parentNotif?.created_by?.avatar_url} name={parentNotif?.created_by?.email} className={css.avatar} />
                    <div className={css.description} onClick={() => redirectUser(parentNotif, NOTIFICATION_CATEGORIES.FOLDER)}>
                      <DeleteIcon
                        className={css.deleteIcon}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNotifDelete(parentNotif.id);
                        }}
                      />
                      <div style={{ fontWeight: parentNotif?.isRead ? '400' : '600' }}>
                        <div className={css.whoTagged}>
                          {parentNotif?.created_by?.username ? parentNotif?.created_by?.username : (canViewEmails ? parentNotif?.created_by?.email : 'User')}{' '}
                        </div>{' '}
                        {parentNotif?.message}
                      </div>
                      <div className={css.date}>{formatRelativeTime(new Date(parentNotif.created_at))}</div>
                    </div>
                  </div>
                </div>
              </div>
            );
          }
        })}
      </div>
      <div className={css.footer}>
        <div
          className={css.markRead}
          onClick={() => {
            if (notifications.length === 0) return;
            onDeleteAll();
          }}
        >
          Clear All
        </div>
        <ContainedButton disabled={notifications.length === 0} kind="purple" className={css.clearAll} onClick={readAllMessages}>
          Mark all as read <CheckIcon />
        </ContainedButton>
      </div>
      {loading && <LinearProgress />}
    </div>
  );
};

export default Notifications;
